"use client";

import Star from "@mui/icons-material/Star";
import StarHalf from "@mui/icons-material/StarHalf";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { LUXE_ACCENT } from "../luxe-shared/keyframes";

export interface TestimonialItem {
  rating: number;
  quote: string;
  avatar: string;
  name: string;
  meta: string;
  tone?: "primary" | "secondary" | "accent";
  offset?: boolean;
}

export interface TestimonialGridLuxeProps {
  title?: string;
  items?: TestimonialItem[];
}

const RatingRow: React.FC<{ value: number; color: string }> = ({ value, color }) => {
  const stars = [];
  const full = Math.floor(value);
  const half = value % 1 >= 0.5;
  for (let i = 0; i < full; i++) {
    stars.push(<Star key={i} sx={{ fontSize: 18, color }} />);
  }
  if (half) {
    stars.push(<StarHalf key="half" sx={{ fontSize: 18, color }} />);
  }
  const empty = 5 - stars.length;
  for (let i = 0; i < empty; i++) {
    stars.push(<Star key={`e-${i}`} sx={{ fontSize: 18, color: `${color}40` }} />);
  }
  return (
    <Stack direction="row" spacing={0.25}>
      {stars}
    </Stack>
  );
};

const TestimonialGridLuxe: React.FC<TestimonialGridLuxeProps> = ({ title = "", items = [] }) => {
  const theme = useTheme();

  const toneColor = (tone: TestimonialItem["tone"]) => {
    if (tone === "secondary") return theme.palette.secondary.main;
    if (tone === "accent") return LUXE_ACCENT;
    return theme.palette.primary.main;
  };

  const borderTone = (tone: TestimonialItem["tone"]) => `${toneColor(tone)}1a`;

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
        bgcolor: "#f8fafc",
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        <Typography
          component="h2"
          sx={{
            fontWeight: 800,
            fontStyle: "italic",
            textAlign: "center",
            textTransform: "uppercase",
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            color: "text.primary",
            mb: { xs: 6, md: 10 },
          }}
        >
          {title}
        </Typography>

        <Grid container spacing={3}>
          {items.map((item) => {
            const c = toneColor(item.tone);
            return (
              <Grid item xs={12} md={4} key={item.name}>
                <Stack
                  spacing={3}
                  sx={{
                    p: 5,
                    borderRadius: "3rem",
                    border: `1px solid ${borderTone(item.tone)}`,
                    bgcolor: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(16px)",
                    height: "100%",
                    transform: item.offset ? { md: "translateY(48px)" } : undefined,
                  }}
                >
                  <RatingRow value={item.rating} color={c} />
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontStyle: "italic",
                      fontWeight: 500,
                      lineHeight: 1.7,
                    }}
                  >
                    &ldquo;{item.quote}&rdquo;
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ pt: 3, borderTop: "1px solid #f1f5f9" }}
                  >
                    <Box
                      component="img"
                      src={item.avatar}
                      alt={item.name}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: `2px solid ${c}4d`,
                        p: 0.5,
                      }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: "text.primary" }}>
                        {item.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "10px",
                          fontWeight: 900,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: c,
                          mt: 0.5,
                        }}
                      >
                        {item.meta}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default TestimonialGridLuxe;
